/**
 * Magnetic activityBar sash (架构方案 §5.7).
 *
 * This one sash is platform-owned rather than delegated to dockview: changing
 * constraints mid-gesture makes dockview end its drag session. Pointer capture
 * keeps one uninterrupted gesture across the 60 <-> 162 magnetic jumps. Each
 * jump is a requestAnimationFrame `setSize` tween so logical and visual width
 * stay the same.
 *
 * After a sash or restore, idle constraints lock `min==max` on the committed
 * stop so a window resize cannot pull the rail off it. The gesture unlocks
 * `[min, max]` for the drag. Dockview marks a locked sash `dv-disabled`; the
 * trailing-sash class keeps the resize cursor in CSS.
 */
import type { DockviewApi } from 'dockview-react';

import {
  beginRailDrag,
  magneticTweenWidth,
  RAIL_LABEL_SIZE,
  type RailDragState,
  type RailIdleCommit,
  railIdleCommitFromGesture,
  railIdleCommitFromWidth,
  railIdleConstraints,
  railIdlePinWidth,
  snapRestoredRailWidth,
  updateRailDrag,
} from '../activity/rail-mode';
import { findAreaTrailingSash } from './area-classes';
import { AREA_SPECS } from './area-specs';

function railBounds(): { min: number; label: number; max: number } {
  const { minSize, maxSize } = AREA_SPECS.activityBar;
  if (minSize === undefined || maxSize === undefined) {
    throw new Error('activityBar AREA_SPEC must declare minSize and maxSize');
  }
  return { min: minSize, label: RAIL_LABEL_SIZE, max: maxSize };
}

function activityBarGroup(api: DockviewApi) {
  return api.getPanel('activityBar')?.group.api;
}

type RailGroupApi = NonNullable<ReturnType<typeof activityBarGroup>>;

function isActivityBarResizeSash(
  api: DockviewApi,
  target: EventTarget | null,
): boolean {
  const sash = target instanceof Element ? target.closest('.dv-sash') : null;
  if (!(sash instanceof HTMLElement)) {
    return false;
  }
  return findAreaTrailingSash(api, 'activityBar') === sash;
}

function setRailRange(groupApi: RailGroupApi, min: number, max: number): void {
  groupApi.setConstraints({
    minimumWidth: min,
    maximumWidth: max,
  });
}

interface MagneticTween {
  raf: number;
  groupApi: RailGroupApi;
  from: number;
  to: number;
  startedAt: number;
}

let tween: MagneticTween | undefined;
let pendingFollow:
  | {
      groupApi: RailGroupApi;
      width: number;
    }
  | undefined;
let idleCommit: RailIdleCommit | undefined;
let railGestureActive = false;

function prefersReducedMotion(): boolean {
  return (
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function cancelTween(): void {
  if (tween === undefined) {
    return;
  }
  if (typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(tween.raf);
  }
  tween = undefined;
}

function applyIdleRange(groupApi: RailGroupApi): void {
  if (railGestureActive || tween !== undefined) {
    return;
  }
  const { min, label } = railBounds();
  const commit = idleCommit ?? railIdleCommitFromWidth(groupApi.width, label);
  idleCommit = commit;
  const range = railIdleConstraints(commit, min, label);
  setRailRange(groupApi, range.min, range.max);
  const target = railIdlePinWidth(commit, min, label);
  if (Math.abs(groupApi.width - target) >= 1) {
    groupApi.setSize({ width: target });
  }
}

function applyPendingFollow(): void {
  const follow = pendingFollow;
  pendingFollow = undefined;
  if (follow && Math.abs(follow.groupApi.width - follow.width) >= 1) {
    follow.groupApi.setSize({ width: follow.width });
  }
  if (follow) {
    applyIdleRange(follow.groupApi);
  }
}

function stopMagneticTween(): void {
  cancelTween();
  pendingFollow = undefined;
}

function tick(now: number): void {
  const current = tween;
  if (!current) {
    return;
  }
  const width = magneticTweenWidth(
    current.from,
    current.to,
    now - current.startedAt,
  );
  current.groupApi.setSize({ width });
  if (Math.abs(width - current.to) < 0.5) {
    tween = undefined;
    current.groupApi.setSize({ width: current.to });
    applyPendingFollow();
    applyIdleRange(current.groupApi);
    return;
  }
  current.raf = requestAnimationFrame(tick);
}

function startMagneticTween(groupApi: RailGroupApi, to: number): void {
  pendingFollow = undefined;
  cancelTween();
  const from = groupApi.width;
  if (
    Math.abs(from - to) < 1 ||
    prefersReducedMotion() ||
    typeof requestAnimationFrame !== 'function'
  ) {
    groupApi.setSize({ width: to });
    applyIdleRange(groupApi);
    return;
  }
  const next: MagneticTween = {
    raf: 0,
    groupApi,
    from,
    to,
    startedAt: performance.now(),
  };
  tween = next;
  next.raf = requestAnimationFrame(tick);
}

function setRailWidth(
  groupApi: RailGroupApi,
  width: number,
  kind: 'magnetic' | 'follow',
): void {
  if (kind === 'magnetic') {
    startMagneticTween(groupApi, width);
    return;
  }
  if (tween !== undefined) {
    pendingFollow = { groupApi, width };
    return;
  }
  groupApi.setSize({ width });
}

/** Snap a leftover width to the nearer stop and adopt that stop. */
export function snapRailAreaSize(api: DockviewApi): void {
  const { min, label } = railBounds();
  const groupApi = activityBarGroup(api);
  if (!groupApi) {
    return;
  }
  const size = snapRestoredRailWidth(groupApi.width, min, label);
  const snapped = Math.abs(groupApi.width - size) >= 1;
  idleCommit = railIdleCommitFromWidth(size, label);
  if (snapped) {
    setRailWidth(groupApi, size, 'magnetic');
    return;
  }
  applyIdleRange(groupApi);
}

/** Re-apply the last idle band after `configureAreas` or a structural restore. */
export function applyRailIdleConstraints(api: DockviewApi): void {
  const groupApi = activityBarGroup(api);
  if (!groupApi) {
    return;
  }
  applyIdleRange(groupApi);
}

interface RailGesture {
  pointerId: number;
  sash: HTMLElement;
  state: RailDragState;
}

/**
 * Install the activityBar magnetic sash. Safe to call once per dockview ready;
 * sash DOM is resolved on each pointerdown. Returns a disposer.
 */
export function installRailSnap(api: DockviewApi): { dispose(): void } {
  const { min, label, max } = railBounds();
  let gesture: RailGesture | null = null;

  const onDown = (event: PointerEvent): void => {
    if (event.button !== 0) {
      return;
    }
    if (!isActivityBarResizeSash(api, event.target)) {
      return;
    }
    const groupApi = activityBarGroup(api);
    if (!groupApi) {
      return;
    }
    const sash =
      event.target instanceof Element ? event.target.closest('.dv-sash') : null;
    if (!(sash instanceof HTMLElement)) {
      return;
    }

    // Own this sash from pointerdown. Letting dockview start and then pinning
    // constraints at a stop makes its internal drag session terminate, so the
    // pointer could not jump back in the same gesture.
    event.preventDefault();
    event.stopImmediatePropagation();
    railGestureActive = true;
    setRailRange(groupApi, min, max);
    const state = beginRailDrag(event.clientX, groupApi.width, min, label);
    if (state.width !== groupApi.width) {
      setRailWidth(groupApi, state.width, 'magnetic');
    }
    gesture = {
      pointerId: event.pointerId,
      sash,
      state,
    };
    try {
      sash.setPointerCapture(event.pointerId);
    } catch {
      // Document capture listeners below still keep an in-window drag usable.
    }
  };

  const onMove = (event: PointerEvent): void => {
    if (!gesture || event.pointerId !== gesture.pointerId) {
      return;
    }
    const groupApi = activityBarGroup(api);
    if (!groupApi) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const next = updateRailDrag(gesture.state, event.clientX, min, label);
    if (next === gesture.state) {
      return;
    }
    const kind = next.phase !== gesture.state.phase ? 'magnetic' : 'follow';
    gesture.state = next;
    setRailWidth(groupApi, next.width, kind);
  };

  const onUp = (event: PointerEvent): void => {
    if (!gesture || event.pointerId !== gesture.pointerId) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const current = gesture;
    const groupApi = activityBarGroup(api);
    const state = updateRailDrag(current.state, event.clientX, min, label);
    idleCommit = railIdleCommitFromGesture(state.phase);
    railGestureActive = false;
    gesture = null;
    if (groupApi) {
      if (Math.abs(groupApi.width - state.width) >= 1) {
        setRailWidth(groupApi, state.width, 'follow');
      }
      applyIdleRange(groupApi);
    }
    if (current.sash.hasPointerCapture(event.pointerId)) {
      current.sash.releasePointerCapture(event.pointerId);
    }
  };

  const onCancel = (event: PointerEvent): void => {
    if (!gesture || event.pointerId !== gesture.pointerId) {
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const current = gesture;
    idleCommit = railIdleCommitFromGesture(current.state.phase);
    railGestureActive = false;
    gesture = null;
    const groupApi = activityBarGroup(api);
    if (groupApi) {
      if (Math.abs(groupApi.width - current.state.width) >= 1) {
        setRailWidth(groupApi, current.state.width, 'follow');
      }
      applyIdleRange(groupApi);
    }
    if (current.sash.hasPointerCapture(event.pointerId)) {
      current.sash.releasePointerCapture(event.pointerId);
    }
  };

  document.addEventListener('pointerdown', onDown, true);
  document.addEventListener('pointermove', onMove, true);
  document.addEventListener('pointerup', onUp, true);
  document.addEventListener('pointercancel', onCancel, true);

  return {
    dispose: () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onCancel, true);
      stopMagneticTween();
      railGestureActive = false;
      if (gesture) {
        const current = gesture;
        idleCommit = railIdleCommitFromGesture(current.state.phase);
        gesture = null;
        const groupApi = activityBarGroup(api);
        if (groupApi) {
          setRailWidth(groupApi, current.state.width, 'follow');
          applyIdleRange(groupApi);
        }
        if (current.sash.hasPointerCapture(current.pointerId)) {
          current.sash.releasePointerCapture(current.pointerId);
        }
      }
      idleCommit = undefined;
    },
  };
}
